<script>
/**
 * @component PracticeCatalogList
 * @description Listado del maestro de prestaciones con stats, búsqueda, filtros por tipo/subtipo/categoría,
 *              pills de cobertura PMO / autorización / cargo usuario, fila expandible con paneles de detalle,
 *              acciones de crear, editar, eliminar e importación masiva desde Excel.
 * @example <PracticeCatalogList />
 */
import practiceCatalogService from "@/services/practice-catalog";
import Spinner from "@/components/ui/Spinner";
import ModalPracticeCatalog from "./ModalPracticeCatalog.vue";
import debounce from "debounce";
import { mapGetters } from "vuex";

const DEBOUNCE_MS = 500;

const TYPE_LABELS = {
  practice: "Práctica",
  treatment: "Tratamiento",
  medication: "Medicamento",
  supply: "Insumo",
  prosthesis: "Prótesis",
  other: "Otro",
};

const CATEGORY_LABELS = {
  consultation: "Consulta",
  lab: "Laboratorio",
  imaging: "Imagen",
  surgery: "Cirugía",
  dental: "Odontología",
  mental_health: "Salud mental",
  rehabilitation: "Rehabilitación",
  emergency: "Emergencia",
  hospitalization: "Internación",
  maternity: "Maternidad",
  medication: "Medicamento",
  supply: "Insumo",
  prosthesis: "Prótesis",
  other: "Otro",
};

const PMO_INCLUDED_OPTIONS = [
  { value: "", label: "Todos" },
  { value: "yes", label: "Incluido" },
  { value: "conditional", label: "Condicionado" },
  { value: "partial", label: "Parcial" },
  { value: "no", label: "No cubre" },
];

const PMO_AUTH_OPTIONS = [
  { value: "", label: "Todas" },
  { value: "yes", label: "Requiere" },
  { value: "no", label: "No requiere" },
];

const CHARGE_TYPE_OPTIONS = [
  { value: "", label: "Todos" },
  { value: "copago", label: "Copago" },
  { value: "coseguro", label: "Coseguro" },
  { value: "arancel", label: "Arancel" },
  { value: "none", label: "Sin cargo" },
];

export default {
  name: "PracticeCatalogList",

  components: {
    Spinner,
    ModalPracticeCatalog,
  },

  data() {
    return {
      isLoading: false,
      searchInput: "",
      filterType: "",
      filterSubtype: "",
      filterCategory: "",
      filterPmoIncluded: "",
      filterPmoAuth: "",
      filterChargeType: "",
      practices: [],
      total: 0,
      currentPage: 1,
      pageSize: 20,
      isModalVisible: false,
      selectedPractice: null,
      expandedRowId: null,
      isImportVisible: false,
      importFile: null,
      importType: "practice",
      importCategory: "consultation",
      isImporting: false,
      importResult: null,
      typeOptions: Object.entries(TYPE_LABELS).map(([value, label]) => ({
        value,
        label,
      })),
      categoryOptions: Object.entries(CATEGORY_LABELS).map(
        ([value, label]) => ({ value, label })
      ),
      pmoIncludedOptions: PMO_INCLUDED_OPTIONS,
      pmoAuthOptions: PMO_AUTH_OPTIONS,
      chargeTypeOptions: CHARGE_TYPE_OPTIONS,
    };
  },

  computed: {
    ...mapGetters(["isAdmin"]),
  },

  created() {
    this.fetchPractices();
  },

  methods: {
    /**
     * @description Obtiene las prestaciones del backend con los filtros aplicados
     */
    async fetchPractices() {
      this.isLoading = true;
      try {
        const params = {
          offset: (this.currentPage - 1) * this.pageSize,
          limit: this.pageSize,
        };
        if (this.searchInput) params.searchInput = this.searchInput;
        if (this.filterType) params.type = this.filterType;
        if (this.filterSubtype) params.subtype = this.filterSubtype;
        if (this.filterCategory) params.category = this.filterCategory;
        if (this.filterPmoIncluded) params.pmoIncluded = this.filterPmoIncluded;
        if (this.filterPmoAuth)
          params.pmoRequiresAuthorization = this.filterPmoAuth;
        if (this.filterChargeType)
          params.userChargeType = this.filterChargeType;

        const result = await practiceCatalogService.query(params);
        this.practices = result.docs;
        this.total = result.total;
      } catch (error) {
        this.$message.error("Error al cargar prestaciones");
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },

    debounceSearch: debounce(function () {
      this.currentPage = 1;
      this.fetchPractices();
    }, DEBOUNCE_MS),

    onFilterChange() {
      this.currentPage = 1;
      this.fetchPractices();
    },

    /**
     * @description Aplica filtro de pill (PMO, Auth o Charge)
     * @param {string} filterKey - Nombre de la propiedad data
     * @param {string} value - Valor del filtro
     */
    setPillFilter(filterKey, value) {
      this[filterKey] = this[filterKey] === value ? "" : value;
      this.currentPage = 1;
      this.fetchPractices();
    },

    onPageChange(page) {
      this.currentPage = page;
      this.fetchPractices();
    },

    /**
     * @description Alterna la fila expandida
     * @param {string} id - ID de la prestación
     */
    toggleRow(id) {
      this.expandedRowId = this.expandedRowId === id ? null : id;
    },

    openModal(practice = null) {
      this.selectedPractice = practice;
      this.isModalVisible = true;
    },

    closeModal() {
      this.isModalVisible = false;
      this.selectedPractice = null;
    },

    onSaved() {
      this.closeModal();
      this.fetchPractices();
    },

    async deletePractice(practice) {
      try {
        await this.$confirm(
          `¿Desea eliminar la prestación "${practice.name}" (${practice.code})?`,
          "Eliminar",
          {
            confirmButtonText: "Aceptar",
            cancelButtonText: "Cancelar",
            type: "warning",
          }
        );
        await practiceCatalogService.remove(practice._id);
        this.$message.success("Prestación eliminada");
        this.fetchPractices();
      } catch {
        // Cancelado
      }
    },

    getTypeLabel(type) {
      return TYPE_LABELS[type] || type || "-";
    },

    getCategoryLabel(category) {
      return CATEGORY_LABELS[category] || category || "-";
    },

    /**
     * @description Devuelve la clase CSS del badge de tipo
     * @param {string} type
     * @returns {string}
     */
    getTypeBadgeClass(type) {
      const map = {
        practice: "pcl-badge--practice",
        treatment: "pcl-badge--treatment",
      };
      return map[type] || "pcl-badge--other";
    },

    /**
     * @description Devuelve la clase CSS del tag PMO
     * @param {string} included - Valor de pmo.included
     * @returns {string}
     */
    getPmoTagClass(included) {
      const map = {
        yes: "pcl-pmo-tag--yes",
        conditional: "pcl-pmo-tag--cond",
        partial: "pcl-pmo-tag--partial",
        no: "pcl-pmo-tag--no",
      };
      return map[included] || "pcl-pmo-tag--no";
    },

    /**
     * @description Devuelve el label del PMO included
     * @param {string} included
     * @returns {string}
     */
    getPmoLabel(included) {
      const map = {
        yes: "Incluido",
        conditional: "Condicionado",
        partial: "Parcial",
        no: "No incluido",
      };
      return map[included] || "Sin datos";
    },

    /**
     * @description Devuelve la clase del tag de cargo usuario
     * @param {Object} charge - userCharge object
     * @returns {string}
     */
    getChargeTagClass(charge) {
      if (!charge || !charge.applies || charge.type === "none")
        return "pcl-charge-tag--no";
      return "pcl-charge-tag--yes";
    },

    /**
     * @description Devuelve el label del cargo usuario
     * @param {Object} charge - userCharge object
     * @returns {string}
     */
    getChargeLabel(charge) {
      if (!charge || !charge.applies || charge.type === "none")
        return "Sin cargo";
      const map = {
        copago: "Copago",
        coseguro: "Coseguro",
        arancel: "Arancel",
      };
      return map[charge.type] || "Sin cargo";
    },

    /**
     * @description Parsea las fuentes (URLs separadas por ;) en un array
     * @param {string} source
     * @returns {string[]}
     */
    parseSources(source) {
      if (!source) return [];
      return source
        .split(";")
        .map((s) => s.trim())
        .filter(Boolean);
    },

    formatPrice(price) {
      if (!price && price !== 0) return "-";
      return `$${price.toLocaleString("es-AR")}`;
    },

    // --- Importación masiva ---

    /**
     * @description Abre el dialog de importación masiva
     */
    openImport() {
      this.isImportVisible = true;
      this.importFile = null;
      this.importResult = null;
      this.importType = "practice";
      this.importCategory = "consultation";
    },

    closeImport() {
      this.isImportVisible = false;
      this.importFile = null;
      this.importResult = null;
    },

    /**
     * @description Captura el archivo seleccionado por el usuario
     * @param {Event} event - Evento del input file
     */
    onFileSelected(event) {
      const file = event.target.files[0];
      if (file) {
        this.importFile = file;
        this.importResult = null;
      }
    },

    /**
     * @description Ejecuta la importación masiva al backend
     */
    async executeImport() {
      if (!this.importFile) {
        this.$message.warning("Seleccioná un archivo primero");
        return;
      }

      this.isImporting = true;
      this.importResult = null;

      try {
        const result = await practiceCatalogService.import(
          this.importFile,
          this.importType,
          this.importCategory
        );
        this.importResult = result;

        if (result.imported > 0) {
          this.$message.success(
            `Se importaron ${result.imported} prestaciones`
          );
          this.fetchPractices();
        }

        if (result.duplicates > 0) {
          this.$message.warning(
            `${result.duplicates} registros duplicados (ya existían)`
          );
        }
      } catch (error) {
        const msg =
          error.response?.data?.error || "Error al importar el archivo";
        this.$message.error(msg);
      } finally {
        this.isImporting = false;
      }
    },
  },
};
</script>

<template lang="pug">
  section.pcl-page

    //- Page header con breadcrumb y total
    .pcl-header
      .pcl-header__left
        router-link.pcl-header__back(:to="{ name: 'settings' }")
          micon(name="arrow_back" size="16")
          span Configuración
        span.pcl-header__sep /
        h1.pcl-header__title Prestaciones
        span.pcl-header__count(v-if="total") ({{ total.toLocaleString('es-AR') }})

    //- Toolbar fila 1: búsqueda + tipo + subtipo + categoría
    .pcl-toolbar.pcl-toolbar--top
      el-input.pcl-toolbar__search(
        prefix-icon="el-icon-search"
        v-model="searchInput"
        @input="debounceSearch"
        placeholder="Buscar por nombre o código"
        :clearable="true"
        size="small"
      )
      el-select(
        v-model="filterType"
        placeholder="Tipo"
        clearable
        @change="onFilterChange"
        size="small"
      )
        el-option(
          v-for="opt in typeOptions"
          :key="opt.value"
          :label="opt.label"
          :value="opt.value"
        )
      el-input.pcl-toolbar__subtype(
        v-model="filterSubtype"
        @input="debounceSearch"
        placeholder="Subtipo"
        :clearable="true"
        size="small"
      )
      el-select(
        v-model="filterCategory"
        placeholder="Categoría"
        clearable
        @change="onFilterChange"
        size="small"
      )
        el-option(
          v-for="opt in categoryOptions"
          :key="opt.value"
          :label="opt.label"
          :value="opt.value"
        )
      .pcl-toolbar__spacer
      .button.button--outline(@click="openImport()" v-if="isAdmin")
        micon(name="cloud_upload").button__icon
        span.button__text Importar Excel
      .button.button--blue(@click="openModal()" v-if="isAdmin")
        micon(name="add_circle_outline").button__icon
        span.button__text Nueva Prestación

    //- Toolbar fila 2: pills de filtro
    .pcl-toolbar.pcl-toolbar--bottom
      .pcl-pills
        span.pcl-pills__label Cobertura
        span.pcl-pill(
          v-for="opt in pmoIncludedOptions"
          :key="'pmo-' + opt.value"
          :class="{ 'pcl-pill--active': filterPmoIncluded === opt.value }"
          @click="setPillFilter('filterPmoIncluded', opt.value)"
        ) {{ opt.label }}
      .pcl-toolbar__divider
      .pcl-pills
        span.pcl-pills__label Autorización
        span.pcl-pill(
          v-for="opt in pmoAuthOptions"
          :key="'auth-' + opt.value"
          :class="{ 'pcl-pill--active': filterPmoAuth === opt.value }"
          @click="setPillFilter('filterPmoAuth', opt.value)"
        ) {{ opt.label }}
      .pcl-toolbar__divider
      .pcl-pills
        span.pcl-pills__label Cargo usuario
        span.pcl-pill(
          v-for="opt in chargeTypeOptions"
          :key="'charge-' + opt.value"
          :class="{ 'pcl-pill--active': filterChargeType === opt.value }"
          @click="setPillFilter('filterChargeType', opt.value)"
        ) {{ opt.label }}

    //- Tabla
    .pcl-table-wrap
      table.pcl-table
        thead
          tr
            th Código
            th Nombre / Subtipo
            th Tipo
            th Cobertura PMO
            th Cargo usuario
            th Acciones
        tbody
          spinner(v-if="isLoading")
          template(v-else v-for="practice in practices")
            tr.pcl-row(
              :key="practice._id"
              :class="{ 'pcl-row--expanded': expandedRowId === practice._id }"
              @click="toggleRow(practice._id)"
            )
              td.pcl-td-code {{ practice.code }}
              td.pcl-td-name
                .pcl-td-name__main {{ practice.name }}
                .pcl-td-name__sub(v-if="practice.subtype") {{ practice.subtype }}
              td
                span.pcl-badge(:class="getTypeBadgeClass(practice.type)") {{ getTypeLabel(practice.type) }}
              td.pcl-td-pmo
                .pcl-pmo-tag(:class="getPmoTagClass(practice.pmo && practice.pmo.included)")
                  span.pcl-pmo-tag__dot
                  | {{ getPmoLabel(practice.pmo && practice.pmo.included) }}
                .pcl-pmo-auth(v-if="practice.pmo && practice.pmo.requiresAuthorization")
                  micon(name="shield" size="12")
                  | Requiere autorización
              td
                span.pcl-charge-tag(:class="getChargeTagClass(practice.userCharge)")
                  | {{ getChargeLabel(practice.userCharge) }}
              td.pcl-td-actions(@click.stop)
                button.pcl-action-btn(@click="openModal(practice)" title="Editar")
                  micon(name="edit" size="14")
                button.pcl-action-btn.pcl-action-btn--danger(
                  @click="deletePractice(practice)"
                  v-if="isAdmin"
                  title="Eliminar"
                )
                  micon(name="delete_forever" size="14")

            //- Fila expandida con 3 paneles
            tr.pcl-expanded-row(
              v-if="expandedRowId === practice._id"
              :key="'exp-' + practice._id"
            )
              td(colspan="6")
                .pcl-expanded
                  //- Panel 1: Identificación
                  .pcl-panel
                    .pcl-panel__title
                      micon(name="assignment" size="13")
                      | Identificación
                    .pcl-info-row
                      span.pcl-info-row__label Código
                      span.pcl-info-row__value.pcl-info-row__value--mono {{ practice.code }}
                    .pcl-info-row
                      span.pcl-info-row__label Tipo
                      span.pcl-info-row__value {{ getTypeLabel(practice.type) }}
                    .pcl-info-row
                      span.pcl-info-row__label Subtipo
                      span.pcl-info-row__value {{ practice.subtype || '-' }}
                    .pcl-info-row
                      span.pcl-info-row__label Categoría
                      span.pcl-info-row__value {{ getCategoryLabel(practice.category) }}

                  //- Panel 2: Cobertura PMO
                  .pcl-panel
                    .pcl-panel__title
                      micon(name="shield" size="13")
                      | Cobertura PMO
                    .pcl-info-row
                      span.pcl-info-row__label Cobertura
                      span.pcl-pmo-tag.pcl-pmo-tag--sm(:class="getPmoTagClass(practice.pmo && practice.pmo.included)")
                        span.pcl-pmo-tag__dot
                        | {{ getPmoLabel(practice.pmo && practice.pmo.included) }}
                    .pcl-info-row
                      span.pcl-info-row__label Tope anual
                      span.pcl-info-row__value {{ (practice.pmo && practice.pmo.maxPerYear) || 'Sin tope específico' }}
                    .pcl-info-row
                      span.pcl-info-row__label Autorización
                      span.pcl-info-row__value(
                        :class="{ 'pcl-info-row__value--warn': practice.pmo && practice.pmo.requiresAuthorization }"
                      ) {{ practice.pmo && practice.pmo.requiresAuthorization ? 'Requerida' : 'No requerida' }}
                    .pcl-info-row.pcl-info-row--col(v-if="practice.pmo && practice.pmo.note")
                      span.pcl-info-row__label Condición
                      span.pcl-info-row__note {{ practice.pmo.note }}
                    template(v-if="practice.pmo && practice.pmo.source")
                      a.pcl-source-link(
                        v-for="(src, idx) in parseSources(practice.pmo.source)"
                        :key="'pmo-src-' + idx"
                        :href="src"
                        target="_blank"
                        rel="noopener"
                      )
                        micon(name="open_in_new" size="10")
                        | Ver fuente normativa

                  //- Panel 3: Cargo al usuario
                  .pcl-panel
                    .pcl-panel__title
                      micon(name="paid" size="13")
                      | Cargo al usuario
                    .pcl-info-row
                      span.pcl-info-row__label Aplica cargo
                      span.pcl-charge-tag.pcl-charge-tag--sm(:class="getChargeTagClass(practice.userCharge)")
                        | {{ practice.userCharge && practice.userCharge.applies ? 'Sí — ' + getChargeLabel(practice.userCharge) : 'Sin cargo' }}
                    .pcl-info-row.pcl-info-row--col(v-if="practice.userCharge && practice.userCharge.note")
                      span.pcl-info-row__label Detalle
                      span.pcl-info-row__note {{ practice.userCharge.note }}
                    template(v-if="practice.userCharge && practice.userCharge.source")
                      a.pcl-source-link(
                        v-for="(src, idx) in parseSources(practice.userCharge.source)"
                        :key="'uc-src-' + idx"
                        :href="src"
                        target="_blank"
                        rel="noopener"
                      )
                        micon(name="open_in_new" size="10")
                        | Ver resolución

      //- Paginación
      el-pagination(
        v-if="total > pageSize"
        layout="prev, pager, next"
        :total="total"
        :page-size="pageSize"
        :current-page="currentPage"
        @current-change="onPageChange"
      )

    ModalPracticeCatalog(
      v-if="isModalVisible"
      :practice="selectedPractice"
      @close="closeModal"
      @saved="onSaved"
    )

    //- Dialog de importación masiva
    el-dialog(title="Importar Prestaciones desde Excel" :visible.sync="isImportVisible" width="550px")
      .import-form
        p.import-help El archivo debe tener como mínimo las columnas #[strong Código] y #[strong Nombre]. Columnas opcionales: Descripción, Tipo, Categoría, Unidad, Precio, Complejidad, Notas.

        el-form(label-position="top" size="small")
          el-row(:gutter="16")
            el-col(:span="12")
              el-form-item(label="Tipo por defecto")
                el-select(v-model="importType" placeholder="Tipo")
                  el-option(
                    v-for="opt in typeOptions"
                    :key="opt.value"
                    :label="opt.label"
                    :value="opt.value"
                  )
            el-col(:span="12")
              el-form-item(label="Categoría por defecto")
                el-select(v-model="importCategory" placeholder="Categoría")
                  el-option(
                    v-for="opt in categoryOptions"
                    :key="opt.value"
                    :label="opt.label"
                    :value="opt.value"
                  )

          el-form-item(label="Archivo Excel o CSV")
            input.file-input(
              type="file"
              accept=".xlsx,.xls,.csv"
              @change="onFileSelected"
            )

        //- Resultado de la importación
        .import-result(v-if="importResult")
          el-alert(
            :title="`Importación finalizada: ${importResult.imported} de ${importResult.total} registros importados`"
            :type="importResult.imported === importResult.total ? 'success' : 'warning'"
            :closable="false"
            show-icon
          )
          p(v-if="importResult.duplicates > 0")
            | {{ importResult.duplicates }} registros omitidos por código duplicado.
          .import-errors(v-if="importResult.errors && importResult.errors.length > 0")
            p.import-errors__title Errores:
            ul
              li(v-for="(err, idx) in importResult.errors" :key="idx") {{ err }}

      template(#footer)
        el-button(@click="closeImport") Cerrar
        el-button(
          type="primary"
          @click="executeImport"
          :loading="isImporting"
          :disabled="!importFile"
        ) Importar
</template>

<style lang="scss" scoped>
$navy: #0b1f3a;
$teal: #00b4a6;
$teal-light: #e6faf9;
$amber: #f59e0b;
$amber-light: #fef3c7;
$red: #ef4444;
$red-light: #fee2e2;
$green: #10b981;
$green-light: #d1fae5;
$gray-50: #f8fafc;
$gray-100: #f1f5f9;
$gray-200: #e2e8f0;
$gray-300: #cbd5e1;
$gray-400: #94a3b8;
$gray-500: #64748b;
$gray-600: #475569;
$gray-700: #334155;
$gray-900: #0f172a;

// ─── Header ───
.pcl-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;

  &__left {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__back {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 13px;
    color: $gray-500;
    text-decoration: none;
    transition: color 0.15s;

    .micon {
      fill: $gray-400;
      width: 16px;
      height: 16px;
    }

    &:hover {
      color: $navy;
      .micon {
        fill: $navy;
      }
    }
  }

  &__sep {
    font-size: 13px;
    color: $gray-300;
  }

  &__title {
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 20px;
    font-weight: 700;
    color: $navy;
    margin: 0;
    letter-spacing: -0.3px;
  }

  &__count {
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 14px;
    font-weight: 600;
    color: $gray-400;
  }
}

// ─── Toolbar ───
.pcl-toolbar {
  background: #fff;
  border: 1.5px solid $gray-200;
  padding: 12px 14px;
  display: flex;
  align-items: center;
  gap: 10px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  flex-wrap: wrap;

  &--top {
    border-radius: 10px 10px 0 0;
    border-bottom: none;
    margin-bottom: 0;
  }

  &--bottom {
    border-radius: 0 0 10px 10px;
    border-top: 1px solid $gray-100;
    padding: 10px 14px;
    gap: 14px;
    margin-bottom: 12px;
  }

  &__search {
    width: 250px;
  }

  &__subtype {
    width: 160px;
  }

  &__spacer {
    flex: 1;
  }

  &__divider {
    width: 1px;
    height: 24px;
    background: $gray-200;
    flex-shrink: 0;
  }

  .el-select {
    width: 160px;
  }
}

// ─── Pills ───
.pcl-pills {
  display: flex;
  gap: 6px;
  align-items: center;

  &__label {
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: $gray-400;
    margin-right: 2px;
    white-space: nowrap;
  }
}

.pcl-pill {
  padding: 3px 8px;
  border-radius: 999px;
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  border: 1.5px solid $gray-200;
  background: $gray-100;
  color: $gray-600;
  transition: all 0.12s;
  white-space: nowrap;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  }

  &--active {
    background: $navy;
    color: #fff;
    border-color: $navy;
  }
}

// ─── Table ───
.pcl-table-wrap {
  background: #fff;
  border: 1.5px solid $gray-200;
  border-radius: 14px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}

.pcl-table {
  width: 100%;
  border-collapse: collapse;

  thead th {
    padding: 11px 16px;
    text-align: left;
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: $gray-500;
    background: $gray-50;
    border-bottom: 1.5px solid $gray-200;
    white-space: nowrap;
  }

  thead th:first-child {
    padding-left: 20px;
  }

  td {
    padding: 8px 16px;
    vertical-align: top;
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 12px;
  }

  td:first-child {
    padding-left: 20px;
  }
}

.pcl-row {
  border-bottom: 1px solid $gray-100;
  transition: background 0.1s;
  cursor: pointer;

  &:last-child {
    border-bottom: none;
  }
  &:hover {
    background: #f8fbff;
  }
  &--expanded {
    background: #f0f7ff;
  }
}

.pcl-td-code {
  font-family: "DM Mono", monospace;
  font-size: 11.5px;
  font-weight: 500;
  color: $navy;
  white-space: nowrap;
}

.pcl-td-name {
  max-width: 340px;

  &__main {
    font-size: 12px;
    font-weight: 600;
    color: $gray-900;
    line-height: 1.3;
  }

  &__sub {
    font-size: 10.5px;
    color: $gray-400;
    margin-top: 2px;
    font-style: italic;
  }
}

// ─── Badges ───
.pcl-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: 999px;
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: 10.5px;
  font-weight: 600;
  white-space: nowrap;

  &--practice {
    background: #eff6ff;
    color: #1d4ed8;
  }
  &--treatment {
    background: #f5f3ff;
    color: #6d28d9;
  }
  &--other {
    background: $gray-100;
    color: $gray-600;
  }
}

// ─── PMO tag ───
.pcl-pmo-tag {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 8px;
  border-radius: 6px;
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;

  &--yes {
    background: $green-light;
    color: $green;
  }
  &--cond {
    background: $amber-light;
    color: #92400e;
  }
  &--partial {
    background: $red-light;
    color: $red;
  }
  &--no {
    background: $gray-100;
    color: $gray-500;
  }

  &__dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  &--yes .pcl-pmo-tag__dot {
    background: $green;
  }
  &--cond .pcl-pmo-tag__dot {
    background: $amber;
  }
  &--partial .pcl-pmo-tag__dot {
    background: $red;
  }
  &--no .pcl-pmo-tag__dot {
    background: $gray-400;
  }

  &--sm {
    padding: 2px 7px;
    font-size: 11.5px;
  }
}

.pcl-pmo-auth {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: 10px;
  color: $amber;
  font-weight: 600;
  margin-top: 3px;

  .micon {
    fill: $amber;
    width: 12px;
    height: 12px;
  }
}

// ─── Charge tag ───
.pcl-charge-tag {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 8px;
  border-radius: 6px;
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: 11px;
  font-weight: 600;

  &--yes {
    background: $amber-light;
    color: #92400e;
  }
  &--no {
    background: $gray-100;
    color: $gray-500;
  }

  &--sm {
    padding: 2px 7px;
    font-size: 11.5px;
  }
}

// ─── Actions ───
.pcl-td-actions {
  white-space: nowrap;
}

.pcl-action-btn {
  width: 30px;
  height: 30px;
  border-radius: 6px;
  border: 1.5px solid $gray-200;
  background: #fff;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.12s;
  margin-right: 4px;

  .micon {
    fill: $gray-600;
  }

  &:hover {
    border-color: $teal;
    .micon {
      fill: $teal;
    }
  }

  &--danger:hover {
    border-color: $red;
    .micon {
      fill: $red;
    }
  }
}

// ─── Expanded row ───
.pcl-expanded-row td {
  background: #f0f7ff;
  padding: 0;
  border-bottom: 2px solid $teal;
}

.pcl-expanded {
  padding: 16px 20px 20px;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 16px;
}

.pcl-panel {
  background: #fff;
  border: 1.5px solid $gray-200;
  border-radius: 10px;
  padding: 14px 16px;

  &__title {
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: $gray-400;
    margin-bottom: 10px;
    display: flex;
    align-items: center;
    gap: 6px;

    .micon {
      fill: $gray-400;
    }
  }
}

.pcl-info-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
  padding: 5px 0;
  border-bottom: 1px solid $gray-100;
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: 12.5px;

  &:last-child {
    border-bottom: none;
  }

  &--col {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  &__label {
    color: $gray-500;
    flex-shrink: 0;
  }

  &__value {
    color: $gray-700;
    font-weight: 500;
    text-align: right;

    &--mono {
      font-family: "DM Mono", monospace;
    }
    &--warn {
      color: $amber;
    }
  }

  &__note {
    font-size: 11.5px;
    color: $gray-600;
    font-weight: 400;
    line-height: 1.4;
    max-width: 260px;
  }
}

.pcl-source-link {
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: 11px;
  color: #00897b;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin-top: 6px;

  .micon {
    fill: #00897b;
    width: 10px;
    height: 10px;
  }

  &:hover {
    text-decoration: underline;
  }
}

// ─── Import dialog ───
.import-form {
  padding: 0 5px;
}

.import-help {
  margin-bottom: 16px;
  color: #606266;
  font-size: 13px;
  line-height: 1.5;
}

.file-input {
  width: 100%;
  padding: 8px 0;
}

.import-result {
  margin-top: 16px;
}

.import-errors {
  margin-top: 10px;
  max-height: 150px;
  overflow-y: auto;
  font-size: 12px;
  color: #f56c6c;

  &__title {
    font-weight: bold;
    margin-bottom: 4px;
  }

  ul {
    padding-left: 20px;
  }
}

// ─── Pagination ───
.pcl-table-wrap + .el-pagination {
  margin-top: 0;
  padding: 14px 20px;
}

// ─── Button overrides (existing project styles) ───
.button--outline {
  background: transparent;
  border: 1px solid #409eff;
  color: #409eff;

  &:hover {
    background: #ecf5ff;
  }
}
</style>
